import React, { useState } from "react";
import { useSelector } from "react-redux";
import { isMobile } from "react-device-detect";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import moment from "moment-timezone";
import cn from "classnames";

import {
  getProcessingSpeed,
  getOrderStatus,
  getOrderError,
  getMainProduct,
  handleProductStatus,
  getLogOfStatuses,
} from "_helpers";

import breakpoints from "_constants/breakpoints";

import OrderImg from "assets/img/Clip-OrderId.png";
import OrderStatusImg from "assets/img/Clip-OrderStatus.png";
import ProcessingSpeedImg from "assets/img/Clip-ProcessingSpeed.png";
import OrderCreatedImg from "assets/img/Clip-OrderCreated.png";
import { ReactComponent as ErrorImg } from "assets/img/Error.svg";

const Container = styled.div`
  display: flex;
  justify-content: center;
  flex-direction: row;
  width: 100%;
  height: 326px;
  padding: 48px 0;
  background-color: #ffffff;
  border-top: 1px solid #ecedf1;
  border-bottom: 1px solid #ecedf1;
  margin-bottom: 74px;

  ${({ theme: { mobile } }) =>
    mobile
      ? css`
          flex-direction: column;
          height: 100%;
          padding: 48px;
          margin-bottom: 0;

          @media (max-width: ${breakpoints.mobileM}) {
            padding: 32px 16px;
          }
        `
      : css`
          @media (max-width: 1350px) {
            flex-wrap: wrap;
            justify-content: space-evenly;
            gap: 24px;
            height: 100%;
          }
        `}
`;
const Card = styled.div`
  display: flex;
  flex-direction: column;
  width: 323px;
  height: 230px;
  box-shadow: 0px 3px 25px rgba(168, 168, 168, 0.3);
  border-bottom: none;

  ${({ theme: { mobile } }) =>
    mobile &&
    css`
      flex-direction: column;
      width: 100%;
      height: auto;
      border-bottom: 10px solid #e5effe;
    `}

  &:not(:last-child) {
    ${({ theme: { mobile } }) =>
      mobile
        ? css`
            margin-bottom: 14px;
          `
        : css`
            margin-right: 24px;

            @media (max-width: 1350px) {
              margin-right: 0;
            }
          `}
  }
`;
const ImageContainer = styled.div`
  position: relative;
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ theme: { mobile } }) =>
    mobile
      ? css`
          width: 100%;
          min-height: 180px;
        `
      : css`
          height: 180px;
        `}
`;
const InfoContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;

  ${({ theme: { mobile } }) =>
    mobile
      ? css`
          flex-direction: column;
          align-items: center;
          flex: 1;
          padding: 10px;
        `
      : css`
          height: 50px;
          background-color: #fafaff;
          line-height: 50px;
        `}
`;
const Label = styled.div`
  font-family: "Montserrat";
  font-size: 14px;
  text-align: center;
  color: #03589c;

  ${({ mWidth }) =>
    mWidth &&
    css`
      min-width: ${mWidth}px;
    `}
`;
const Data = styled(Label)`
  font-family: "Montserrat";

  ${({ theme: { mobile } }) =>
    !mobile &&
    css`
      margin-left: 3px;
      margin-right: 10px;
      line-height: 24px;
    `}
`;

const ErrorContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  box-shadow: 0 2px 0 #03589c;
  cursor: pointer;

  &:hover {
    background-color: #03589c11;
  }
`;
const ErrorIconContainer = styled.div`
  min-width: 16px;
  max-width: 16px;
  line-height: 16px;

  svg {
    color: var(--bs-red);
    width: 100%;
    height: 100%;
  }
`;
const ErrorText = styled.div`
  line-height: 16px;
  text-align: start;
`;
const ErrorDetails = styled.div`
  overflow-y: auto;
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  padding: 8px;
  color: #7b848d;
  background-color: #ffffffdd;
  border-bottom: 2px solid #7b848d55;
  transform: translateY(-100%);
  transition: transform 0.5s;

  &.active {
    transform: translateY(0%);
  }

  ${({ theme: { mobile } }) =>
    mobile
      ? css`
          text-align: center;
        `
      : css`
          text-align: start;
        `}
`;
const ErrorDetailsTitle = styled.div`
  font-size: 1rem;
  font-weight: bold;
  border-bottom: 1px solid #7b848d55;
  margin-bottom: 4px;
`;
const ErrorDetailsText = styled.div`
  font-size: 0.875rem;
`;

function GeneralInfo({ orderDetails }) {
  const [showErrorDetails, setShowErrorDetails] = useState(false);

  const { processingStatusesObj, processingErrors } = useSelector(({ constant }) => constant);

  const mainProduct = getMainProduct(orderDetails.products);
  const productStatus = getOrderStatus(orderDetails.products);
  const productError = getOrderError(orderDetails.products);
  const logOfStatuses = getLogOfStatuses(orderDetails.products);
  const { status: handledStatus, error: handledError } = handleProductStatus({
    status: productStatus,
    error: productError,
    logOfStatuses,
  });
  const status = (processingStatusesObj && processingStatusesObj[handledStatus]) || handledStatus || "N/A";
  const error = processingErrors?.find((item) => item.code === handledError);

  return (
    <Container>
      <Card>
        <ImageContainer>
          <img
            alt=""
            src={OrderImg}
            // width={isMobile ? 100 : 200}
            // height={isMobile ? 67 : 133}
          />
        </ImageContainer>
        <InfoContainer>
          <Label>Order: </Label>
          {isMobile && "\n\r"}
          <Data>{orderDetails.crmOrderId}</Data>
        </InfoContainer>
      </Card>
      <Card>
        <ImageContainer>
          <img
            alt=""
            src={OrderStatusImg}
            // width={isMobile ? 100 : 200}
            // height={isMobile ? 75 : 150}
          />
          <ErrorDetails className={cn({ active: showErrorDetails })}>
            <ErrorDetailsTitle>Error Description</ErrorDetailsTitle>
            <ErrorDetailsText>
              {error?.description || "There is no special description for this error"}
            </ErrorDetailsText>
          </ErrorDetails>
        </ImageContainer>
        <InfoContainer>
          <Label mWidth={120}>Order Status: </Label>
          {isMobile && "\n\r"}
          <Data>
            {error ? (
              <ErrorContainer
                title="Click to see details"
                onClick={() => {
                  setShowErrorDetails((prev) => !prev);
                }}
              >
                <ErrorIconContainer>
                  <ErrorImg />
                </ErrorIconContainer>
                <ErrorText>{status}</ErrorText>-<ErrorText>{error?.value || productError}</ErrorText>
              </ErrorContainer>
            ) : (
              status
            )}
          </Data>
        </InfoContainer>
      </Card>
      <Card>
        <ImageContainer>
          <img
            alt=""
            src={ProcessingSpeedImg}
            // width={isMobile ? 75 : 150}
            // height={isMobile ? 75 : 150}
          />
        </ImageContainer>
        <InfoContainer>
          <Label>Processing speed: </Label>
          {isMobile && "\n\r"}
          <Data>{getProcessingSpeed(orderDetails.products)}</Data>
        </InfoContainer>
      </Card>
      <Card>
        <ImageContainer>
          <img
            alt=""
            src={OrderCreatedImg}
            // width={isMobile ? 110 : 220}
            // height={isMobile ? 67 : 147}
          />
        </ImageContainer>
        <InfoContainer>
          <Label>Order Placed: </Label>
          {isMobile && "\n\r"}
          <Data>{moment(mainProduct.paymentDate).format("MMM DD, YYYY")}</Data>
        </InfoContainer>
      </Card>
    </Container>
  );
}

GeneralInfo.propTypes = {
  orderDetails: PropTypes.exact({
    accountManager: PropTypes.object,
    crmOrderId: PropTypes.string,
    parentOrderUid: PropTypes.string,
    orderDate: PropTypes.string,
    uid: PropTypes.string,
    products: PropTypes.arrayOf(PropTypes.object),
    productCodes: PropTypes.arrayOf(PropTypes.object),
    paymentDetails: PropTypes.object,
    notes: PropTypes.arrayOf(PropTypes.object),
    files: PropTypes.arrayOf(PropTypes.object),
  }),
};

export default GeneralInfo;
